/**
 * themeCustomizationMappers — pure boundary contract for theme persistence.
 *
 * Maps between the UI-domain `ThemeCustomization` (used by the Settings
 * panel + CSS application layer) and the canonical backend-valid persisted
 * shape (validated by the auth profile endpoint).
 *
 * This module is deliberately pure — no React, Redux, or DOM dependencies —
 * so it can be unit-tested by Testify without the frontend runtime stack.
 */

export const FONT_OPTIONS = [
  { value: 'inter', label: 'Inter', css: "'Inter', sans-serif" },
  { value: 'system', label: 'System Default', css: 'system-ui, -apple-system, sans-serif' },
  { value: 'mono', label: 'JetBrains Mono', css: "'JetBrains Mono', monospace" },
  { value: 'serif', label: 'Georgia', css: 'Georgia, serif' },
  { value: 'sans', label: 'Open Sans', css: "'Open Sans', sans-serif" },
] as const;

export const BORDER_RADIUS_OPTIONS = [
  { value: 'sharp', label: 'Sharp', css: '0' },
  { value: 'rounded', label: 'Rounded', css: '0.5rem' },
  { value: 'pill', label: 'Pill', css: '9999px' },
] as const;

export interface ThemeCustomization {
  fontFamily: string;
  accentColor: string;
  textSize: string;
  borderRadius: string;
}

export const DEFAULTS: ThemeCustomization = {
  fontFamily: 'inter',
  accentColor: '#3b82f6',
  textSize: 'medium',
  borderRadius: 'rounded',
};

export type CurrentThemeMode = 'light' | 'dark' | 'system';

export interface PersistedThemeCustomization {
  mode: 'light' | 'dark' | 'auto';
  fontFamily: (typeof FONT_OPTIONS)[number]['value'];
  accentColor: string;
  textSize: 'sm' | 'md' | 'lg';
  borderRadius: (typeof BORDER_RADIUS_OPTIONS)[number]['value'];
}

export function isValidHex(hex: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

/**
 * Map a UI-domain `ThemeCustomization` to the backend-valid persisted shape.
 * Write-boundary mapping — preserves existing UI labels/CSS scales unchanged.
 */
export function mapThemeCustomizationForPersistence(
  config: ThemeCustomization,
  currentMode: CurrentThemeMode,
): PersistedThemeCustomization {
  const fontFamily = FONT_OPTIONS.find(option => option.value === config.fontFamily)?.value
    ?? FONT_OPTIONS[0].value;
  const borderRadius = BORDER_RADIUS_OPTIONS.find(option => option.value === config.borderRadius)?.value
    ?? BORDER_RADIUS_OPTIONS[1].value;
  const textSize: PersistedThemeCustomization['textSize'] = config.textSize === 'small'
    ? 'sm'
    : config.textSize === 'large'
      ? 'lg'
      : 'md';

  return {
    mode: currentMode === 'system' ? 'auto' : currentMode,
    fontFamily,
    accentColor: isValidHex(config.accentColor) ? config.accentColor : DEFAULTS.accentColor,
    textSize,
    borderRadius,
  };
}

/**
 * Read-boundary inverse of `mapThemeCustomizationForPersistence`. Translates the
 * canonical backend `textSize: sm|md|lg` back to the UI-domain
 * `small|medium|large` so the Settings S/M/L control stays selected and
 * `--text-scale` keeps the 0.875/1/1.125 mapping. Unknown values fall back to
 * UI defaults without throwing.
 */
export function mapThemeCustomizationFromPersistence(
  persisted: PersistedThemeCustomization,
): ThemeCustomization {
  const fontFamily = FONT_OPTIONS.find(option => option.value === persisted.fontFamily)?.value
    ?? DEFAULTS.fontFamily;
  const borderRadius = BORDER_RADIUS_OPTIONS.find(option => option.value === persisted.borderRadius)?.value
    ?? DEFAULTS.borderRadius;
  const textSize: ThemeCustomization['textSize'] = persisted.textSize === 'sm'
    ? 'small'
    : persisted.textSize === 'lg'
      ? 'large'
      : 'medium';
  const accentColor = isValidHex(persisted.accentColor)
    ? persisted.accentColor
    : DEFAULTS.accentColor;

  return {
    fontFamily,
    accentColor,
    textSize,
    borderRadius,
  };
}
